import { apiClient } from "@/lib/api";
import { filterParams, filtersOf, type FilterSearch } from "@/lib/columnFilters";
import type { PaginatedResponse } from "@/lib/types";
import { listLimit } from "@/lib/pageSize";
import type { Changed } from "@/components/table/changedColumns";

// Warehouse → Plekken, Artikelen, Instellingen (docs/design_handoff_wms/README.md §4). Reading is
// wms_masterdata, changing places and articles wms_masterdata_write, the settings admin (O3) —
// all enforced server-side. Voorraad (docs/wms-datamodel.md §5): reading wms_stock, blocking and
// releasing pallets wms_stock_write.

// ─── Warehouses ──────────────────────────────────────────────────────────────

export interface WarehouseAddress {
  id: string;
  code: string;
  name: string;
  street: string | null;
  houseNumber: string | null;
  houseNumberAddition: string | null;
  postalCode: string | null;
  city: string;
  countryCode: string;
}

export interface Warehouse {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  timezone: string;
  locationCount: number;
  /** Its address: a place in Organisaties → Locaties. */
  location: WarehouseAddress | null;
  /** Only where vehicles or drivers are based. */
  branch: { id: string; code: string; name: string } | null;
}

export interface WarehouseList {
  warehouses: Warehouse[];
  /** The one this person chose last, else the first. */
  preferredId: string | null;
}

export function listWarehouses(): Promise<WarehouseList> {
  return apiClient.get<WarehouseList>("/api/v1/warehouse/warehouses");
}

export function updateWarehouse(
  id: string,
  input: { name?: string; locationId?: string | null; branchId?: string | null; isActive?: boolean },
): Promise<Warehouse> {
  return apiClient.patch<Warehouse>(`/api/v1/warehouse/warehouses/${id}`, input);
}

/** "Marshallweg 8, Veghel". */
export function addressLine(address: WarehouseAddress): string {
  const street = [address.street, [address.houseNumber, address.houseNumberAddition].filter(Boolean).join("")].filter(Boolean).join(" ");
  return [street, address.city].filter(Boolean).join(", ");
}

export function rememberWarehouse(warehouseId: string): Promise<{ warehouseId: string }> {
  return apiClient.put("/api/v1/warehouse/preference", { warehouseId });
}

// ─── The tree and its places ─────────────────────────────────────────────────

export interface TreeRack {
  rack: string | null;
  count: number;
}
export interface TreeAisle {
  aisle: string | null;
  count: number;
  racks: TreeRack[];
}
export interface TreeZone {
  zone: string | null;
  count: number;
  /** What every place in the zone shares: cooled, frozen, hazardous. */
  properties: string[];
  aisles: TreeAisle[];
}
export interface WarehouseTree {
  warehouse: { id: string; code: string; name: string };
  total: number;
  zones: TreeZone[];
  floor: number;
  special: number;
}

export function getWarehouseTree(warehouseId: string): Promise<WarehouseTree> {
  return apiClient.get<WarehouseTree>(`/api/v1/warehouse/warehouses/${warehouseId}/tree`);
}

/** A node of the tree, as it rides in the address: "-" is a place without that part. */
export interface NodeSearch {
  zone?: string;
  aisle?: string;
  rack?: string;
  group?: string;
}

export interface PlaceSummary {
  total: number;
  active: number;
  occupied: number;
  blocked: number;
  levels: number;
  placesPerLevel: number;
  batchPure: number;
  batchPureBottomOnly: boolean;
}

function nodeQuery(node: NodeSearch): Record<string, string | undefined> {
  return { zone: node.zone, aisle: node.aisle, rack: node.rack, group: node.group };
}

export function getPlaceSummary(warehouseId: string, node: NodeSearch): Promise<PlaceSummary> {
  return apiClient.get<PlaceSummary>("/api/v1/warehouse/storage-locations/summary", { warehouseId, ...nodeQuery(node) });
}

export type PlaceStatus = "free" | "occupied" | "blocked" | "out_of_use";

export interface StoragePlace extends Changed {
  id: string;
  warehouseId: string;
  barcode: string;
  name: string;
  zone: string | null;
  aisle: string | null;
  rack: string | null;
  level: string | null;
  position: string | null;
  kind: string;
  properties: string[];
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  maxWeightKg: number | null;
  singlePallet: boolean;
  batchPure: boolean;
  /** How many pallets fit: the fill meter of putting away on the scanner. */
  maxPallets: number | null;
  /** Looproute: the scanner walks the picks from low to high, a place without one last. */
  pickSequence: number | null;
  isActive: boolean;
  status: PlaceStatus;
  block: { reason: string; remark: string | null; createdAt: string } | null;
  unit: { id: string; barcode: string } | null;
  unitCount: number;
  createdAt: string;
}

export interface PlaceBlock {
  id: string;
  reason: string;
  remark: string | null;
  source: string;
  createdAt: string;
  createdBy: string | null;
  liftedAt: string | null;
  liftedBy: string | null;
  liftRemark: string | null;
}

export interface StoragePlaceDetail extends StoragePlace {
  units: {
    id: string;
    barcode: string;
    kind: string;
    description: string | null;
    batch: string | null;
    quantity: number | null;
    /** What is on it (the first three lines), and how many lines there are. */
    lines: { batch: string | null; article: { code: string; description: string } }[];
    lineCount: number;
  }[];
  blocks: PlaceBlock[];
}

export type PlacesSearch = FilterSearch &
  NodeSearch & {
    warehouse?: string;
    q?: string;
    cursor?: string;
    limit?: string;
    sort?: string;
    order?: string;
  };

export const PLACES_TABLE = "storage-locations";

export function listPlaces(warehouseId: string, search: PlacesSearch): Promise<PaginatedResponse<StoragePlace>> {
  return apiClient.get<PaginatedResponse<StoragePlace>>("/api/v1/warehouse/storage-locations", {
    warehouseId,
    ...nodeQuery(search),
    q: search.q,
    cursor: search.cursor,
    limit: listLimit(search, PLACES_TABLE),
    sort: search.sort,
    order: search.order,
    ...filterParams(filtersOf(search)),
  });
}

export function getPlace(id: string): Promise<StoragePlaceDetail> {
  return apiClient.get<StoragePlaceDetail>(`/api/v1/warehouse/storage-locations/${id}`);
}

export interface PlaceInput {
  warehouseId: string;
  name: string;
  kind: string;
  zone?: string | null;
  aisle?: string | null;
  rack?: string | null;
  level?: string | null;
  position?: string | null;
  properties?: string[];
  maxWeightKg?: number | null;
  heightCm?: number | null;
  batchPure?: boolean;
  singlePallet?: boolean;
}

export function createPlace(input: PlaceInput): Promise<StoragePlace> {
  return apiClient.post<StoragePlace>("/api/v1/warehouse/storage-locations", input);
}

export interface SeriesInput {
  warehouseId: string;
  zone: string | null;
  aisle: string;
  rackFrom: number;
  rackTo: number;
  levels: number;
  positions: number;
  kind: string;
  batchPure: "none" | "bottom" | "all";
  singlePallet: boolean;
  properties: string[];
  maxWeightKg: number | null;
  heightCm: number | null;
}

export interface SeriesSummary {
  count: number;
  first: string[];
  last: string | null;
  existing: string[];
  existingCount: number;
}

export function previewSeries(input: SeriesInput): Promise<SeriesSummary> {
  return apiClient.post<SeriesSummary>("/api/v1/warehouse/storage-locations/series", { ...input, dryRun: true });
}

export function createSeries(input: SeriesInput): Promise<SeriesSummary> {
  return apiClient.post<SeriesSummary>("/api/v1/warehouse/storage-locations/series", input);
}

export interface PlaceChanges {
  kind?: string;
  properties?: string[];
  maxWeightKg?: number | null;
  heightCm?: number | null;
  batchPure?: boolean;
  singlePallet?: boolean;
  maxPallets?: number | null;
  isActive?: boolean;
}

export function updatePlaces(ids: string[], changes: PlaceChanges): Promise<{ updated: number; unchanged: number }> {
  return apiClient.patch("/api/v1/warehouse/storage-locations", { ids, ...changes });
}

/** Looproute: number the places in the order of their names from start, or take them off the route (null). */
export function setWalkOrder(
  ids: string[],
  start: number | null,
  step: number,
  descending: boolean,
): Promise<{ updated: number; unchanged: number }> {
  return apiClient.post("/api/v1/warehouse/storage-locations/walk-order", { ids, start, step, descending });
}

export function blockPlaces(ids: string[], reason: string, remark: string): Promise<{ blocked: number; alreadyBlocked: number }> {
  return apiClient.post("/api/v1/warehouse/storage-locations/block", { ids, reason, ...(remark ? { remark } : {}) });
}

export function releasePlaces(ids: string[], remark: string): Promise<{ released: number }> {
  return apiClient.post("/api/v1/warehouse/storage-locations/release", { ids, remark });
}

// ─── Owners and articles ─────────────────────────────────────────────────────

export interface Owner {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  articleCount: number;
}

export function listOwners(q?: string): Promise<Owner[]> {
  return apiClient.get<Owner[]>("/api/v1/warehouse/owners", { q: q || undefined });
}

export function getOwner(id: string): Promise<Owner> {
  return apiClient.get<Owner>(`/api/v1/warehouse/owners/${id}`);
}

export interface ArticleListItem {
  id: string;
  code: string;
  description: string;
  isActive: boolean;
}

export interface ArticleList {
  owner: { id: string; code: string; name: string };
  articles: ArticleListItem[];
  /** How many match the search; the list holds at most 500 of them. */
  total: number;
  /** How many the owner has in all. */
  ownerTotal: number;
}

export function listArticles(ownerId: string, q?: string): Promise<ArticleList> {
  return apiClient.get<ArticleList>("/api/v1/warehouse/articles", { ownerId, q: q || undefined });
}

export type UnitLevel = "cu" | "tu" | "du";
export type Source = "customer" | "wlx";

export interface ArticleUnit {
  id: string;
  level: UnitLevel;
  name: string | null;
  unitOfMeasure: string;
  quantityOfChild: number | null;
  gtin: string | null;
  netWeightKg: number | null;
  grossWeightKg: number | null;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  volumeM3: number | null;
  perLayer: number | null;
  splittable: boolean;
  source: Source;
  /** Of a customer's unit: the fields WLX filled in. */
  wlxFields: string[];
}

export interface ArticlePickLocation {
  id: string;
  warehouse: { id: string; code: string; name: string };
  storageLocation: { id: string; name: string; barcode: string; kind: string; isActive: boolean };
  replenishMode: string | null;
  effectiveReplenishMode: string;
  minQuantity: number | null;
  maxQuantity: number | null;
}

export interface ArticleDetail extends Changed {
  id: string;
  owner: { id: string; code: string; name: string };
  code: string;
  description: string;
  descriptionShort: string | null;
  articleGroupCode: string | null;
  batchRequired: boolean;
  bestBeforeRequired: boolean;
  palletNumberRequired: boolean;
  shelfLifeDays: number | null;
  minRemainingShelfLifeDaysReceipt: number | null;
  minRemainingShelfLifeDaysDespatch: number | null;
  storageProperties: string[];
  hazardousCode: string | null;
  defaultPalletType: { id: string; code: string; description: string } | null;
  /** The smallest unit the floor picks: below it a packaging stays closed. */
  pickUnitLevel: UnitLevel | null;
  source: Source;
  isActive: boolean;
  units: ArticleUnit[];
  pickLocations: ArticlePickLocation[];
  createdAt: string;
}

export function getArticle(id: string): Promise<ArticleDetail> {
  return apiClient.get<ArticleDetail>(`/api/v1/warehouse/articles/${id}`);
}

export interface ArticleFields {
  description?: string;
  descriptionShort?: string | null;
  articleGroupCode?: string | null;
  hazardousCode?: string | null;
  batchRequired?: boolean;
  bestBeforeRequired?: boolean;
  palletNumberRequired?: boolean;
  shelfLifeDays?: number | null;
  minRemainingShelfLifeDaysReceipt?: number | null;
  minRemainingShelfLifeDaysDespatch?: number | null;
  storageProperties?: string[];
  isActive?: boolean;
}

export function createArticle(input: ArticleFields & { ownerPartyId: string; code: string; description: string }): Promise<ArticleDetail> {
  return apiClient.post<ArticleDetail>("/api/v1/warehouse/articles", input);
}

export function updateArticle(id: string, input: ArticleFields): Promise<ArticleDetail> {
  return apiClient.patch<ArticleDetail>(`/api/v1/warehouse/articles/${id}`, input);
}

export type UnitFields = Partial<Omit<ArticleUnit, "id" | "level" | "source" | "wlxFields">>;

export function putUnit(articleId: string, level: UnitLevel, fields: UnitFields): Promise<ArticleDetail> {
  return apiClient.put<ArticleDetail>(`/api/v1/warehouse/articles/${articleId}/units/${level}`, fields);
}

export function deleteUnit(articleId: string, level: UnitLevel): Promise<void> {
  return apiClient.delete<void>(`/api/v1/warehouse/articles/${articleId}/units/${level}`);
}

export interface PickLocationInput {
  storageLocation: string;
  replenishMode?: string | null;
  minQuantity?: number | null;
  maxQuantity?: number | null;
}

export function putPickLocation(articleId: string, warehouseId: string, input: PickLocationInput): Promise<ArticleDetail> {
  return apiClient.put<ArticleDetail>(`/api/v1/warehouse/articles/${articleId}/pick-locations/${warehouseId}`, input);
}

export function deletePickLocation(articleId: string, warehouseId: string): Promise<void> {
  return apiClient.delete<void>(`/api/v1/warehouse/articles/${articleId}/pick-locations/${warehouseId}`);
}

// ─── Settings ────────────────────────────────────────────────────────────────

export interface WarehouseDefaults {
  pickMode: string;
  batchPicking: boolean;
  stackPicking: boolean;
  mergePartials: string;
  oneBatchPerPallet: boolean;
  pickByLocationScan: boolean;
  /** Andere palletsoort bij ontvangst: off for the Campina owners. */
  palletTypeAtReceipt: boolean;
  /** Het PIC-bericht: an order to a country that needs a COKZ inspection gets its batches fixed and reported. */
  pickReport: boolean;
  /** De THT-controle per afnemer: a receiver never gets a shorter best-before than it had, unless the batch is named. */
  deliveredCheck: boolean;
  replenishMode: string;
  shadowMode: boolean; // Scoretrace leads while it is on (handoff ronde 2, decision 32)
  /** A pallet that comes in is scanned onto an expedition place, not put on the receiving place. */
  receiveOnStaging: boolean;
  /** An order is loaded only once it is activated. */
  loadAfterActivation: boolean;
  /** Warehouse → Orders opens on a week of load days or of delivery days. */
  outboundDay: string;
}

export type OwnerSettingKey =
  | "pickMode"
  | "batchPicking"
  | "stackPicking"
  | "mergePartials"
  | "oneBatchPerPallet"
  | "pickByLocationScan"
  | "palletTypeAtReceipt"
  | "pickReport"
  | "deliveredCheck";
export type OwnerValues = { [K in OwnerSettingKey]: WarehouseDefaults[K] | null };

export interface SettingsMatrix {
  warehouse: { id: string; code: string; name: string };
  defaults: WarehouseDefaults;
  owners: { owner: { id: string; code: string; name: string }; values: OwnerValues; deviations: number }[];
}

export interface OwnerSettingsDetail {
  warehouse: { id: string; code: string; name: string };
  owner: { id: string; code: string; name: string };
  defaults: WarehouseDefaults;
  values: OwnerValues;
  deviations: number;
}

export function getSettings(warehouseId: string): Promise<SettingsMatrix> {
  return apiClient.get<SettingsMatrix>("/api/v1/warehouse/settings", { warehouseId });
}

export function getOwnerSettings(warehouseId: string, ownerId: string): Promise<OwnerSettingsDetail> {
  return apiClient.get<OwnerSettingsDetail>(`/api/v1/warehouse/settings/${warehouseId}/owners/${ownerId}`);
}

export function putWarehouseSettings(warehouseId: string, values: Partial<WarehouseDefaults>): Promise<unknown> {
  return apiClient.put(`/api/v1/warehouse/settings/${warehouseId}`, values);
}

export function putOwnerSettings(warehouseId: string, ownerId: string, values: Partial<OwnerValues>): Promise<OwnerSettingsDetail> {
  return apiClient.put<OwnerSettingsDetail>(`/api/v1/warehouse/settings/${warehouseId}/owners/${ownerId}`, values);
}

// ─── Stock ───────────────────────────────────────────────────────────────────

export type StockStatus = "free" | "blocked";

export interface StockBlock {
  /** Where the block sits that stops the line: on itself, its pallet or its place. */
  on: "line" | "unit" | "place";
  reason: string;
  remark: string | null;
  createdAt: string;
}

export interface StockUnit {
  unitOfMeasure: string;
  name: string | null;
}

/** One row of Voorraad: a stock line on a pallet. */
export interface StockLine extends Changed {
  id: string;
  handlingUnitId: string;
  barcode: string;
  state: string | null;
  labelSource: "own" | "customer" | null;
  palletType: string | null;
  /** How many lines the pallet has: more than one is a mixed pallet. */
  lineCount: number;
  owner: { id: string; code: string | null; name: string } | null;
  article: { id: string; code: string; description: string };
  /** In the article's CU. */
  quantity: number;
  cu: StockUnit | null;
  tu: (StockUnit & { quantityOfChild: number | null }) | null;
  tuCount: number | null;
  weightKg: number | null;
  batch: string | null;
  externalBatch: string | null;
  bestBefore: string | null;
  productionDate: string | null;
  receivedAt: string;
  clientStockCode: { code: string; description: string | null } | null;
  location: { id: string; name: string; kind: string } | null;
  status: StockStatus;
  block: StockBlock | null;
}

export type StockSearch = FilterSearch & {
  warehouse?: string;
  q?: string;
  cursor?: string;
  limit?: string;
  sort?: string;
  order?: string;
  /** The pallet open beside the list. */
  pallet?: string;
};

export const STOCK_TABLE = "stock";

function stockQuery(warehouseId: string, search: StockSearch) {
  return { warehouseId, q: search.q, ...filterParams(filtersOf(search)) };
}

export function listStock(warehouseId: string, search: StockSearch): Promise<PaginatedResponse<StockLine>> {
  return apiClient.get<PaginatedResponse<StockLine>>("/api/v1/warehouse/stock", {
    ...stockQuery(warehouseId, search),
    cursor: search.cursor,
    limit: listLimit(search, STOCK_TABLE),
    sort: search.sort,
    order: search.order,
  });
}

export interface StockSummary {
  pallets: number;
  lines: number;
  weightKg: number;
  linesWithoutWeight: number;
}

export function getStockSummary(warehouseId: string, search: StockSearch): Promise<StockSummary> {
  return apiClient.get<StockSummary>("/api/v1/warehouse/stock/summary", stockQuery(warehouseId, search));
}

export interface StockOwner {
  id: string;
  code: string | null;
  name: string;
  pallets: number;
}

export function listStockOwners(warehouseId: string): Promise<StockOwner[]> {
  return apiClient.get<StockOwner[]>("/api/v1/warehouse/stock/owners", { warehouseId });
}

export interface WarehouseEventItem {
  id: string;
  kind: string;
  articleId?: string | null;
  occurredAt: string;
  user: string | null;
  via: string;
  reason: string | null;
  remark: string | null;
  quantity: number | null;
  from: string | null;
  to: string | null;
  data: Record<string, unknown>;
}

export interface PalletDetail extends Changed {
  id: string;
  barcode: string;
  kind: string;
  state: string | null;
  labelSource: "own" | "customer" | null;
  source: string;
  sealNumber: string | null;
  palletSeqInBatch: number | null;
  createdAt: string;
  warehouse: { id: string; code: string; name: string };
  location: { id: string; name: string; kind: string } | null;
  placeBlock: { reason: string; remark: string | null; createdAt: string } | null;
  palletType: { code: string; description: string } | null;
  owner: { id: string; code: string | null; name: string } | null;
  parent: { id: string; barcode: string } | null;
  blocks: PlaceBlock[];
  lines: (StockLine & { serialNumber: string | null; retestDate: string | null; blocks: PlaceBlock[] })[];
  consignments: { number: string; orderId: string }[];
  events: WarehouseEventItem[];
}

export function getPallet(id: string): Promise<PalletDetail> {
  return apiClient.get<PalletDetail>(`/api/v1/warehouse/handling-units/${id}`);
}

export function blockPallets(ids: string[], reason: string, remark: string): Promise<{ blocked: number; alreadyBlocked: number }> {
  return apiClient.post("/api/v1/warehouse/handling-units/block", { ids, reason, ...(remark ? { remark } : {}) });
}

export function releasePallets(ids: string[], remark: string): Promise<{ released: number }> {
  return apiClient.post("/api/v1/warehouse/handling-units/release", { ids, remark });
}

// ─── Corrections and lost & found (docs/wms-datamodel.md §18) ────────────────
// Less than WLX expected goes to lost & found, more comes from it first; writing off and increasing change
// the customer's stock, with the customer's reason code — that is wms_stock_book, the rest wms_stock_write.

/** A customer's reason for writing off or increasing, as Scoretrace had them: "0001 · Missing in WH". */
export interface CorrectionReason {
  id: string;
  code: string;
  description: string;
}

export function listCorrectionReasons(): Promise<{ reasons: CorrectionReason[] }> {
  return apiClient.get<{ reasons: CorrectionReason[] }>("/api/v1/warehouse/correction-reasons");
}

/** Why something went to lost & found: the reasons of a count difference, and a pallet not found. */
export const LOST_REASONS = ["count_error", "wrong_pick", "damaged", "unknown"] as const;
export type LostReason = (typeof LOST_REASONS)[number];

export interface Settled {
  toLostFound: { handlingUnitId: string; barcode: string; quantity: number }[];
  fromLostFound: { barcode: string; quantity: number }[];
  increased: number;
}

export interface LostFoundLine {
  id: string;
  article: { id: string; code: string; description: string };
  batch: string | null;
  bestBefore: string | null;
  quantity: number;
  cu: { unitOfMeasure: string; name: string | null } | null;
  tu: { unitOfMeasure: string; name: string | null; quantityOfChild: number | null } | null;
  tuCount: number | null;
}

export interface LostFoundItem {
  id: string;
  barcode: string;
  /** A part (LF-2026-000123) of what a pallet missed, or a whole pallet that is missing. */
  kind: "part" | "pallet";
  owner: { id: string; name: string } | null;
  place: string | null;
  lines: LostFoundLine[];
  since: string;
  by: string | null;
  reason: string | null;
  remark: string | null;
  from: string | null;
  fromPallet: string | null;
  /** A found pallet waits for the forklift. */
  found: boolean;
}

export function listLostFound(warehouseId: string): Promise<{ items: LostFoundItem[] }> {
  return apiClient.get<{ items: LostFoundItem[] }>("/api/v1/warehouse/lost-found", { warehouseId });
}

export interface LostFoundMatch {
  handlingUnitId: string;
  barcode: string;
  quantity: number;
  batch: string | null;
  from: string | null;
}

export function lostFoundMatches(warehouseId: string, articleId: string, locationId: string | null): Promise<{ matches: LostFoundMatch[] }> {
  return apiClient.get<{ matches: LostFoundMatch[] }>("/api/v1/warehouse/lost-found/matches", { warehouseId, articleId, ...(locationId ? { locationId } : {}) });
}

/** Aantal aanpassen: the new quantity of a line, in CU. */
export function adjustStockLine(id: string, input: { quantity: number; reason: LostReason; remark?: string; bookReasonId?: string }): Promise<Settled> {
  return apiClient.post<Settled>(`/api/v1/warehouse/stock-lines/${id}/adjust`, input);
}

export function palletLost(id: string, remark?: string): Promise<{ barcode: string; from: string | null }> {
  return apiClient.post(`/api/v1/warehouse/handling-units/${id}/lost`, remark ? { remark } : {});
}

export function palletFound(id: string): Promise<{ taskId: string; barcode: string }> {
  return apiClient.post(`/api/v1/warehouse/handling-units/${id}/found`, {});
}

export function returnLostFound(id: string, code: string, remark?: string): Promise<{ part: string; onto: string }> {
  return apiClient.post(`/api/v1/warehouse/handling-units/${id}/return`, { code, ...(remark ? { remark } : {}) });
}

export function writeOff(id: string, input: { reasonId: string; remark?: string; lines?: { id: string; quantity: number }[] }): Promise<{ writtenOff: number; reason: string }> {
  return apiClient.post(`/api/v1/warehouse/handling-units/${id}/write-off`, input);
}

// ─── Query keys ──────────────────────────────────────────────────────────────

export const warehouseKeys = {
  all: ["warehouse"] as const,
  warehouses: ["warehouse", "warehouses"] as const,
  tree: (warehouseId: string) => ["warehouse", "tree", warehouseId] as const,
  summary: (warehouseId: string, node: NodeSearch) => ["warehouse", "summary", warehouseId, node] as const,
  places: (warehouseId: string, search: PlacesSearch) => ["warehouse", "places", warehouseId, search] as const,
  place: (id: string) => ["warehouse", "place", id] as const,
  owners: (q: string) => ["warehouse", "owners", q] as const,
  owner: (id: string) => ["warehouse", "owner", id] as const,
  articles: (ownerId: string, q: string) => ["warehouse", "articles", ownerId, q] as const,
  article: (id: string) => ["warehouse", "article", id] as const,
  settings: (warehouseId: string) => ["warehouse", "settings", warehouseId] as const,
  ownerSettings: (warehouseId: string, ownerId: string) => ["warehouse", "settings", warehouseId, ownerId] as const,
  stock: (warehouseId: string, search: StockSearch) => ["warehouse", "stock", warehouseId, search] as const,
  stockSummary: (warehouseId: string, search: StockSearch) => ["warehouse", "stockSummary", warehouseId, search] as const,
  stockOwners: (warehouseId: string) => ["warehouse", "stockOwners", warehouseId] as const,
  pallet: (id: string) => ["warehouse", "pallet", id] as const,
  lostFound: (warehouseId: string) => ["warehouse", "lostFound", warehouseId] as const,
  correctionReasons: ["warehouse", "correctionReasons"] as const,
};
